import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createLesson } from '../../api/lessons';
import { useI18n } from '../../i18n/I18nContext';
import ModulePicker from './ModulePicker';
import { parseLessonTitles } from './bulkLessons';

/**
 * Many lessons at once: one title per line, all in one module, draft or published. They are
 * created one after another in the order given (the API has no batch route), so a failure stops
 * where it happened and "Add the rest" carries on from that line instead of adding doubles.
 */
function BulkLessonsForm({
  courseId, modules, defaultModuleId, startOrder, onDone, onCancel, onModuleCreated, modulesFailed, onRetryModules,
}) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [moduleId, setModuleId] = useState(defaultModuleId || '');
  const [isPublished, setIsPublished] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  // Where a run stopped: the index of the title that failed, so a retry starts there.
  const [stoppedAt, setStoppedAt] = useState(null);
  const areaRef = useRef(null);

  useEffect(() => { areaRef.current?.focus(); }, []);

  const rows = useMemo(() => parseLessonTitles(text), [text]);
  const invalid = rows.filter((r) => r.error);
  const canSubmit = rows.length > 0 && invalid.length === 0 && !running;

  const run = async (fromIndex) => {
    setRunning(true);
    setStoppedAt(null);
    const total = rows.length;
    for (let i = fromIndex; i < total; i += 1) {
      setProgress({ done: i, total });
      try {
        // eslint-disable-next-line no-await-in-loop
        await createLesson(courseId, {
          title: rows[i].title,
          moduleId: moduleId || null,
          isPublished,
          orderIndex: startOrder + i,
        });
      } catch {
        setStoppedAt(i);
        setRunning(false);
        setProgress(null);
        return;
      }
    }
    setRunning(false);
    setProgress(null);
    onDone(total - fromIndex);
  };

  const submit = (e) => {
    e.preventDefault();
    if (canSubmit) run(0);
  };

  return (
    <section className="section-card les-form-card" aria-labelledby="les-bulk-title">
      <div className="section-head">
        <h3 className="section-head__title" id="les-bulk-title">{t('lessonAuthoring.bulk.title')}</h3>
      </div>
      <form className="les-form" onSubmit={submit}>
        <div className="ui-field les-field">
          <label className="ui-label" htmlFor="les-bulk-input">{t('lessonAuthoring.bulk.label')}</label>
          <textarea
            id="les-bulk-input"
            ref={areaRef}
            value={text}
            onChange={(e) => { setText(e.target.value); setStoppedAt(null); }}
            rows={8}
            placeholder={t('lessonAuthoring.bulk.placeholder')}
            disabled={running || stoppedAt !== null}
            aria-describedby="les-bulk-hint"
          />
          <p className="ui-hint" id="les-bulk-hint">{t('lessonAuthoring.bulk.hint')}</p>
          {invalid.length > 0 && (
            <ul className="les-bulk-errors" role="alert">
              {invalid.slice(0, 5).map((r) => (
                <li key={r.line} className="ui-error">{t(`lessonAuthoring.bulk.${r.error}`, { line: r.line })}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="ui-form-grid les-form-row">
          <div className="ui-field les-field">
            <span className="ui-label" id="les-bulk-module-label">{t('courseContent.lessons.moduleLabel')}</span>
            <ModulePicker
              courseId={courseId}
              modules={modules}
              value={moduleId}
              onChange={setModuleId}
              onCreated={onModuleCreated}
              loadFailed={modulesFailed}
              onRetryLoad={onRetryModules}
              labelledBy="les-bulk-module-label"
            />
          </div>
          <div className="ui-field les-field">
            <span className="ui-label" id="les-bulk-status-label">{t('lessonAuthoring.visibility.label')}</span>
            <VisibilitySwitch value={isPublished} onChange={setIsPublished} labelledBy="les-bulk-status-label" />
          </div>
        </div>
        {stoppedAt !== null && (
          <div className="ui-alert" role="alert">
            {t('lessonAuthoring.bulk.failed', { done: stoppedAt, title: rows[stoppedAt]?.title })}
          </div>
        )}
        <div className="les-form-actions">
          {rows.length > 0 && !running && stoppedAt === null && (
            <span className="les-bulk-summary">{t('lessonAuthoring.bulk.summary', { count: rows.length })}</span>
          )}
          {running && progress && (
            <span className="les-bulk-summary" role="status">
              {t('lessonAuthoring.bulk.progress', { done: progress.done + 1, total: progress.total })}
            </span>
          )}
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onCancel} disabled={running}>
            {t('lessonAuthoring.bulk.cancel')}
          </button>
          {stoppedAt !== null ? (
            <button type="button" className="ds-btn ds-btn-primary" onClick={() => run(stoppedAt)}>
              {t('lessonAuthoring.bulk.retryRest')}
            </button>
          ) : (
            <button type="submit" className="ds-btn ds-btn-primary" disabled={!canSubmit}>
              {t('lessonAuthoring.bulk.submit', { count: rows.length })}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

/** Draft or published, as two segments; the hint under it says what each means for students. */
export function VisibilitySwitch({ value, onChange, labelledBy }) {
  const { t } = useI18n();
  return (
    <>
      <div className="les-segment" role="radiogroup" aria-labelledby={labelledBy}>
        {[false, true].map((published) => (
          <button
            key={String(published)}
            type="button"
            role="radio"
            aria-checked={value === published}
            className={`les-segment-btn${value === published ? ' is-active' : ''}`}
            onClick={() => onChange(published)}
          >
            {published ? t('lessonAuthoring.visibility.published') : t('lessonAuthoring.visibility.draft')}
          </button>
        ))}
      </div>
      <span className="ui-hint">
        {value ? t('lessonAuthoring.visibility.publishedHint') : t('lessonAuthoring.visibility.draftHint')}
      </span>
    </>
  );
}

export default BulkLessonsForm;
